import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash, PencilSimple, DownloadSimple, Eye, EnvelopeSimple, Phone, Cake, GlobeHemisphereWest, IdentificationCard, CalendarBlank } from "@phosphor-icons/react";
import api, { apiError, coverSrc } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";

const ROLE_LABEL = { admin: "Administrateur", professeur: "Professeur", etudiant: "Étudiant" };
const ROLE_COLOR = { admin: "bg-accent", professeur: "bg-primary", etudiant: "bg-sky-600" };
const STATUS_LABEL = { actif: "Actif", inactif: "Inactif", suspendu: "Suspendu" };
const STATUS_COLOR = { actif: "bg-emerald-600 hover:bg-emerald-600", inactif: "bg-secondary text-foreground hover:bg-secondary", suspendu: "bg-accent hover:bg-accent" };
const EMPTY = {
  name: "", civilite: "", nom: "", prenom: "", username: "", email: "", password: "", role: "etudiant", filiere: "", promotion: "", semestre: "",
  phone: "", status: "actif", birth_date: "", nationality: "", student_number: "",
};

function Avatar({ user, size = 40 }) {
  const src = coverSrc(user.avatar);
  if (src) return <img src={src} alt={user.name} className="shrink-0 rounded-full object-cover" style={{ width: size, height: size }} />;
  return (
    <div className="flex shrink-0 items-center justify-center rounded-full bg-primary font-bold text-primary-foreground" style={{ width: size, height: size, fontSize: size * 0.4 }}>
      {user.name?.[0]?.toUpperCase() || "?"}
    </div>
  );
}

export default function Users() {
  const [users, setUsers] = useState([]);
  const [academic, setAcademic] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [editingAvatar, setEditingAvatar] = useState(null);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [filter, setFilter] = useState("all");
  const [viewUser, setViewUser] = useState(null);

  const load = () => {
    api.get("/users").then((r) => setUsers(r.data));
    api.get("/academic").then((r) => setAcademic(r.data));
  };
  useEffect(() => { load(); }, []);

  const filieres = academic.filter((a) => a.type === "filiere");
  const selectedFiliere = filieres.find((f) => f.name === form.filiere);
  const promotions = academic.filter((a) => a.type === "promotion" && (!selectedFiliere || a.parent === selectedFiliere.id));
  const selectedPromotion = promotions.find((p) => p.name === form.promotion);
  const semestres = academic.filter((a) => a.type === "semestre" && (!selectedPromotion || a.parent === selectedPromotion.id));

  const setFiliere = (name) => setForm((f) => ({ ...f, filiere: name, promotion: "", semestre: "" }));
  const setPromotion = (name) => setForm((f) => ({ ...f, promotion: name, semestre: "" }));

  const save = async () => {
    try {
      const fullName = `${form.prenom || ""} ${form.nom || ""}`.trim() || form.name;
      const payload = { ...form, name: fullName };
      if (editId) {
        if (!payload.password) delete payload.password;
        await api.put(`/users/${editId}`, payload);
        toast.success("Utilisateur modifié");
      } else {
        await api.post("/users", payload);
        toast.success("Utilisateur créé");
      }
      setOpen(false); setForm(EMPTY); setEditId(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const edit = (u) => {
    const [fallbackPrenom, ...fallbackNomParts] = (u.name || "").split(" ");
    setForm({
      name: u.name, civilite: u.civilite || "", nom: u.nom || fallbackNomParts.join(" "), prenom: u.prenom || fallbackPrenom || "",
      username: u.username || "", email: u.email, password: "", role: u.role,
      filiere: u.filiere || "", promotion: u.promotion || "", semestre: u.semestre || "",
      phone: u.phone || "", status: u.status || "actif", birth_date: u.birth_date || "",
      nationality: u.nationality || "", student_number: u.student_number || "",
    });
    setEditId(u.id); setEditingAvatar(u.avatar || null); setOpen(true);
  };
  const del = async (id) => { try { await api.delete(`/users/${id}`); toast.success("Supprimé"); load(); } catch (e) { toast.error(apiError(e)); } };
  const uploadAvatar = async (file) => {
    if (!file || !editId) return;
    setAvatarUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/users/${editId}/avatar`, fd);
      setEditingAvatar(data.avatar);
      toast.success("Photo mise à jour");
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setAvatarUploading(false); }
  };
  const downloadTranscript = async (u) => {
    try {
      const res = await api.get(`/users/${u.id}/grades/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url; link.download = `releve-${u.name}.pdf`;
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
  };

  const shown = filter === "all" ? users : users.filter((u) => u.role === filter);

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-accent">Administration</p>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Utilisateurs</h1>
        </div>
        <div className="flex gap-2">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-40" data-testid="role-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les rôles</SelectItem>
              <SelectItem value="admin">Administrateurs</SelectItem>
              <SelectItem value="professeur">Professeurs</SelectItem>
              <SelectItem value="etudiant">Étudiants</SelectItem>
            </SelectContent>
          </Select>
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setForm(EMPTY); setEditId(null); setEditingAvatar(null); } }}>
            <DialogTrigger asChild><Button className="bg-primary hover:bg-primary/90" data-testid="add-user-btn"><Plus size={18} className="mr-2" /> Ajouter</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>{editId ? "Modifier" : "Nouvel"} utilisateur</DialogTitle></DialogHeader>
              <div className="space-y-3">
                {editId && (
                  <div className="flex items-center gap-3">
                    <Avatar user={{ name: form.name, avatar: editingAvatar }} size={56} />
                    <div>
                      <Label className="mb-1 block text-xs">Photo de profil</Label>
                      <Input type="file" accept=".jpg,.jpeg,.png,.webp,image/*" disabled={avatarUploading} onChange={(e) => { if (e.target.files[0]) uploadAvatar(e.target.files[0]); e.target.value = ""; }} data-testid="user-avatar-file" />
                      {avatarUploading && <p className="mt-1 text-xs text-muted-foreground">Envoi en cours...</p>}
                    </div>
                  </div>
                )}
                <div className="flex gap-3">
                  <div className="w-28">
                    <Label>Civilité</Label>
                    <Select value={form.civilite || "__none"} onValueChange={(v) => setForm({ ...form, civilite: v === "__none" ? "" : v })}>
                      <SelectTrigger data-testid="user-civilite"><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none">—</SelectItem>
                        <SelectItem value="M.">M.</SelectItem>
                        <SelectItem value="Mme">Mme</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex-1"><Label>Prénom</Label><Input value={form.prenom} onChange={(e) => setForm({ ...form, prenom: e.target.value })} data-testid="user-prenom" /></div>
                  <div className="flex-1"><Label>Nom</Label><Input value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} data-testid="user-nom" /></div>
                </div>
                <div><Label>Identifiant (connexion)</Label><Input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} autoCapitalize="none" autoCorrect="off" data-testid="user-username" /></div>
                <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email" /></div>
                <div><Label>Mot de passe {editId && "(laisser vide pour ne pas changer)"}</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password" /></div>
                <div className="flex gap-3">
                  <div className="flex-1">
                    <Label>Rôle</Label>
                    <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                      <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="etudiant">Étudiant</SelectItem>
                        <SelectItem value="professeur">Professeur</SelectItem>
                        <SelectItem value="admin">Administrateur</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex-1">
                    <Label>Statut du compte</Label>
                    <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                      <SelectTrigger data-testid="user-status"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="actif">Actif</SelectItem>
                        <SelectItem value="inactif">Inactif</SelectItem>
                        <SelectItem value="suspendu">Suspendu</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {form.status !== "actif" && <p className="text-xs text-accent">Un compte inactif ou suspendu ne peut plus se connecter, mais son historique est conservé.</p>}
                <div className="flex gap-3">
                  <div className="flex-1"><Label>Téléphone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+225 07 00 00 00 00" data-testid="user-phone" /></div>
                  <div className="flex-1"><Label>Date de naissance</Label><Input type="date" value={form.birth_date} onChange={(e) => setForm({ ...form, birth_date: e.target.value })} data-testid="user-birthdate" /></div>
                </div>
                <div className="flex gap-3">
                  <div className="flex-1"><Label>Nationalité</Label><Input value={form.nationality} onChange={(e) => setForm({ ...form, nationality: e.target.value })} placeholder="Ivoirienne" data-testid="user-nationality" /></div>
                  {form.role === "etudiant" && (
                    <div className="flex-1"><Label>Numéro d'étudiant</Label><Input value={form.student_number} onChange={(e) => setForm({ ...form, student_number: e.target.value })} placeholder="ECL-2026-001" data-testid="user-student-number" /></div>
                  )}
                </div>
                <div className="flex gap-3">
                  <div className="flex-1">
                    <Label>Filière</Label>
                    <Select value={form.filiere || "__none"} onValueChange={(v) => setFiliere(v === "__none" ? "" : v)}>
                      <SelectTrigger data-testid="user-filiere"><SelectValue placeholder="Aucune" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none">Aucune</SelectItem>
                        {filieres.map((f) => <SelectItem key={f.id} value={f.name}>{f.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    {filieres.length === 0 && <p className="mt-1 text-xs text-muted-foreground">Aucune filière définie — voir Gestion académique.</p>}
                  </div>
                  <div className="flex-1">
                    <Label>Promotion</Label>
                    <Select value={form.promotion || "__none"} onValueChange={(v) => setPromotion(v === "__none" ? "" : v)} disabled={!selectedFiliere}>
                      <SelectTrigger data-testid="user-promotion"><SelectValue placeholder={selectedFiliere ? "Aucune" : "Choisir une filière d'abord"} /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none">Aucune</SelectItem>
                        {promotions.map((p) => <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label>Semestre</Label>
                  <Select value={form.semestre || "__none"} onValueChange={(v) => setForm({ ...form, semestre: v === "__none" ? "" : v })} disabled={!selectedPromotion}>
                    <SelectTrigger data-testid="user-semestre"><SelectValue placeholder={selectedPromotion ? "Aucun" : "Choisir une promotion d'abord"} /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none">Aucun</SelectItem>
                      {semestres.map((s) => <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter><Button onClick={save} className="bg-primary hover:bg-primary/90" data-testid="save-user-btn">{editId ? "Enregistrer" : "Créer"}</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Vue carte (mobile) */}
      <div className="space-y-3 md:hidden">
        {shown.map((u) => (
          <div key={u.id} className="rounded-xl border border-border bg-card p-4" data-testid={`user-card-${u.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex min-w-0 items-center gap-3">
                <Avatar user={u} />
                <div className="min-w-0">
                  <p className="truncate font-medium">{u.name}</p>
                  <p className="truncate text-sm text-muted-foreground">{u.email}</p>
                </div>
              </div>
              <div className="flex shrink-0 flex-col items-end gap-1">
                <Badge className={`${ROLE_COLOR[u.role]} hover:${ROLE_COLOR[u.role]}`}>{ROLE_LABEL[u.role]}</Badge>
                {(u.status || "actif") !== "actif" && <Badge className={STATUS_COLOR[u.status]}>{STATUS_LABEL[u.status]}</Badge>}
              </div>
            </div>
            <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
              <span>Identifiant : {u.username || "—"}</span>
              <span>Filière : {u.filiere || "—"}</span>
            </div>
            <div className="mt-3 flex justify-end gap-1 border-t border-border pt-3">
              <Button size="icon" variant="ghost" onClick={() => setViewUser(u)} data-testid={`view-user-mobile-${u.id}`}><Eye size={16} /></Button>
              <Button size="icon" variant="ghost" onClick={() => edit(u)} data-testid={`edit-user-mobile-${u.id}`}><PencilSimple size={16} /></Button>
              {u.role === "etudiant" && <Button size="icon" variant="ghost" onClick={() => downloadTranscript(u)} data-testid={`transcript-user-mobile-${u.id}`}><DownloadSimple size={16} /></Button>}
              <Button size="icon" variant="ghost" onClick={() => del(u.id)} data-testid={`del-user-mobile-${u.id}`}><Trash size={16} className="text-accent" /></Button>
            </div>
          </div>
        ))}
        {shown.length === 0 && <p className="py-10 text-center text-sm text-muted-foreground">Aucun utilisateur.</p>}
      </div>

      {/* Vue tableau (desktop) */}
      <div className="hidden rounded-xl border border-border bg-card md:block">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nom</TableHead>
              <TableHead>Identifiant</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Rôle</TableHead>
              <TableHead>Statut</TableHead>
              <TableHead>Filière</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {shown.map((u) => (
              <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                <TableCell className="font-medium">
                  <div className="flex items-center gap-2">
                    <Avatar user={u} size={32} />
                    {u.name}
                  </div>
                </TableCell>
                <TableCell className="text-muted-foreground">{u.username || "—"}</TableCell>
                <TableCell className="text-muted-foreground">{u.email}</TableCell>
                <TableCell><Badge className={`${ROLE_COLOR[u.role]} hover:${ROLE_COLOR[u.role]}`}>{ROLE_LABEL[u.role]}</Badge></TableCell>
                <TableCell><Badge className={STATUS_COLOR[u.status || "actif"]}>{STATUS_LABEL[u.status || "actif"]}</Badge></TableCell>
                <TableCell className="text-muted-foreground">{u.filiere || "—"}</TableCell>
                <TableCell className="text-right">
                  <Button size="icon" variant="ghost" onClick={() => setViewUser(u)} data-testid={`view-user-${u.id}`}><Eye size={16} /></Button>
                  <Button size="icon" variant="ghost" onClick={() => edit(u)} data-testid={`edit-user-${u.id}`}><PencilSimple size={16} /></Button>
                  {u.role === "etudiant" && <Button size="icon" variant="ghost" onClick={() => downloadTranscript(u)} data-testid={`transcript-user-${u.id}`}><DownloadSimple size={16} /></Button>}
                  <Button size="icon" variant="ghost" onClick={() => del(u.id)} data-testid={`del-user-${u.id}`}><Trash size={16} className="text-accent" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!viewUser} onOpenChange={(o) => !o && setViewUser(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto" data-testid="user-detail-dialog">
          {viewUser && (
            <>
              <DialogHeader><DialogTitle>Fiche utilisateur</DialogTitle></DialogHeader>
              <div className="flex items-center gap-4">
                <Avatar user={viewUser} size={72} />
                <div className="min-w-0">
                  <p className="truncate font-display text-xl font-bold">
                    {viewUser.civilite ? `${viewUser.civilite} ` : ""}{viewUser.name}
                  </p>
                  <div className="mt-1 flex flex-wrap gap-1.5">
                    <Badge className={`${ROLE_COLOR[viewUser.role]} hover:${ROLE_COLOR[viewUser.role]}`}>{ROLE_LABEL[viewUser.role]}</Badge>
                    <Badge className={STATUS_COLOR[viewUser.status || "actif"]}>{STATUS_LABEL[viewUser.status || "actif"]}</Badge>
                  </div>
                </div>
              </div>

              <div className="mt-4 space-y-1 rounded-lg border border-border p-4">
                <DetailRow icon={IdentificationCard} label="Identifiant" value={viewUser.username} />
                <DetailRow icon={EnvelopeSimple} label="Email" value={viewUser.email} />
                <DetailRow icon={Phone} label="Téléphone" value={viewUser.phone} />
                <DetailRow icon={Cake} label="Date de naissance" value={viewUser.birth_date} />
                <DetailRow icon={GlobeHemisphereWest} label="Nationalité" value={viewUser.nationality} />
                {viewUser.role === "etudiant" && <DetailRow icon={IdentificationCard} label="Numéro d'étudiant" value={viewUser.student_number} />}
              </div>

              {viewUser.role === "etudiant" && (viewUser.filiere || viewUser.promotion || viewUser.semestre) && (
                <div className="mt-3 space-y-1 rounded-lg border border-border p-4">
                  <DetailRow label="Filière" value={viewUser.filiere} />
                  <DetailRow label="Promotion" value={viewUser.promotion} />
                  <DetailRow label="Semestre" value={viewUser.semestre} />
                </div>
              )}

              <div className="mt-3 rounded-lg border border-border p-4">
                <DetailRow icon={CalendarBlank} label="Compte créé le" value={viewUser.created_at ? new Date(viewUser.created_at).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" }) : null} />
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => { setViewUser(null); edit(viewUser); }} data-testid="view-user-edit-btn">
                  <PencilSimple size={16} className="mr-2" /> Modifier
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DetailRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center justify-between gap-3 border-b border-border/60 py-2 last:border-0">
      <span className="flex items-center gap-2 text-sm text-muted-foreground">
        {Icon && <Icon size={15} />} {label}
      </span>
      <span className="text-sm font-medium">{value || "—"}</span>
    </div>
  );
}
